import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Card from "../../components/common/Card";
import Button from "../../components/common/Button";
import Alert from "../../components/common/Alert";
import PageHeader from "../../components/common/PageHeader";
import Section from "../../components/common/Section";
import {
  useGetRoleQuery,
  useGetPermissionsQuery,
  useGetRolePermissionsQuery,
  useUpdateRolePermissionsMutation,
} from "../../store/api";

const RolePermissionMatrix = () => {
  const { roleId } = useParams();
  const navigate = useNavigate();

  const { data: role } = useGetRoleQuery(roleId);
  const { data: allPermissions, isLoading: loadingPermissions } =
    useGetPermissionsQuery();
  const { data: rolePermissions, isLoading: loadingRolePerms } =
    useGetRolePermissionsQuery(roleId);
  const [updatePermissions, updateState] = useUpdateRolePermissionsMutation();

  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  const permissions = Array.isArray(allPermissions) ? allPermissions : [];
  const currentPermissions = useMemo(
    () => (Array.isArray(rolePermissions) ? rolePermissions : []),
    [rolePermissions],
  );

  useEffect(() => {
    if (currentPermissions.length > 0) {
      const permIds = currentPermissions.map((p) => p.permissionId || p.id);
      setSelectedPermissions(permIds);
    }
  }, [currentPermissions]);

  const handleToggle = (permissionId) => {
    setSelectedPermissions((prev) =>
      prev.includes(permissionId)
        ? prev.filter((id) => id !== permissionId)
        : [...prev, permissionId],
    );
  };

  const handleToggleCategory = (category) => {
    const categoryPerms = groupedPermissions[category] || [];
    const categoryIds = categoryPerms.map((p) => p.permissionId || p.id);
    const allSelected = categoryIds.every((id) =>
      selectedPermissions.includes(id),
    );

    if (allSelected) {
      setSelectedPermissions((prev) =>
        prev.filter((id) => !categoryIds.includes(id)),
      );
    } else {
      setSelectedPermissions((prev) => [...new Set([...prev, ...categoryIds])]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await updatePermissions({
        roleId,
        payload: { permissionIds: selectedPermissions },
      }).unwrap();
      navigate("/roles");
    } catch (err) {
      // error handled by UI
    }
  };

  // Group permissions by category
  const groupedPermissions = permissions.reduce((acc, permission) => {
    const name = permission.name || permission.permissionName || "";
    const category = name.split(".")[0] || "GENERAL";
    if (!acc[category]) acc[category] = [];
    acc[category].push(permission);
    return acc;
  }, {});

  // Filter by search term
  const filteredCategories = Object.keys(groupedPermissions).reduce(
    (acc, category) => {
      const filtered = groupedPermissions[category].filter((p) => {
        const name = p.name || p.permissionName || "";
        const desc = p.description || "";
        return (
          name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          desc.toLowerCase().includes(searchTerm.toLowerCase())
        );
      });
      if (filtered.length > 0) acc[category] = filtered;
      return acc;
    },
    {},
  );

  const isLoading = loadingPermissions || loadingRolePerms;

  return (
    <div className="space-y-6">
      <PageHeader
        title={`Manage Permissions: ${role?.name || role?.roleName || "Role"}`}
        subtitle="Select which permissions this role should have."
      />

      {isLoading && (
        <Card>
          <p className="text-sm text-slate-500">Loading permissions...</p>
        </Card>
      )}

      {!isLoading && (
        <form onSubmit={handleSubmit} className="space-y-6">
          <Card>
            <div className="mb-4">
              <input
                type="text"
                placeholder="Search permissions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="h-11 w-full rounded-lg border border-slate-200 px-4 text-base text-slate-900 shadow-sm outline-none transition focus:border-slate-400"
              />
            </div>
            <div className="flex items-center justify-between rounded-lg bg-slate-50 px-4 py-3">
              <span className="text-sm font-semibold text-slate-700">
                {selectedPermissions.length} of {permissions.length} permissions
                selected
              </span>
              <Button
                type="button"
                size="sm"
                variant="secondary"
                onClick={() =>
                  setSelectedPermissions(
                    selectedPermissions.length === permissions.length
                      ? []
                      : permissions.map((p) => p.permissionId || p.id),
                  )
                }
              >
                {selectedPermissions.length === permissions.length
                  ? "Deselect All"
                  : "Select All"}
              </Button>
            </div>
          </Card>

          {Object.keys(filteredCategories).length === 0 && (
            <Card>
              <p className="text-sm text-slate-500">
                No permissions found matching your search.
              </p>
            </Card>
          )}

          {Object.keys(filteredCategories)
            .sort()
            .map((category) => {
              const categoryPerms = filteredCategories[category];
              const categoryIds = categoryPerms.map(
                (p) => p.permissionId || p.id,
              );
              const allSelected = categoryIds.every((id) =>
                selectedPermissions.includes(id),
              );
              return (
                <Card key={category}>
                  <Section
                    title={
                      <div className="flex items-center justify-between">
                        <span>{category}</span>
                        <button
                          type="button"
                          onClick={() => handleToggleCategory(category)}
                          className="text-sm font-semibold text-[#4f46e5] hover:text-[#4338ca]"
                        >
                          {allSelected ? "Deselect All" : "Select All"}
                        </button>
                      </div>
                    }
                  >
                    <div className="space-y-2">
                      {categoryPerms.map((permission) => {
                        const id = permission.permissionId || permission.id;
                        const name =
                          permission.name || permission.permissionName || "";
                        const isChecked = selectedPermissions.includes(id);

                        return (
                          <label
                            key={id}
                            className="flex cursor-pointer items-start gap-3 rounded-lg border border-slate-100 p-3 transition hover:bg-slate-50"
                          >
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={() => handleToggle(id)}
                              className="mt-1 h-4 w-4 rounded border-slate-300 text-[#4f46e5] focus:ring-[#4f46e5]"
                            />
                            <div className="flex-1">
                              <div className="font-mono text-sm font-semibold text-slate-900">
                                {name}
                              </div>
                              {permission.description && (
                                <div className="mt-1 text-xs text-slate-600">
                                  {permission.description}
                                </div>
                              )}
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  </Section>
                </Card>
              );
            })}

          <Card>
            {updateState.isSuccess && (
              <Alert tone="success" className="mb-4">
                Permissions updated successfully.
              </Alert>
            )}
            {updateState.isError && (
              <Alert tone="error" className="mb-4">
                {updateState.error?.data?.detail ||
                  "Failed to update permissions."}
              </Alert>
            )}

            <div className="flex gap-3">
              <Button type="submit" disabled={updateState.isLoading}>
                Save Permissions
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={() => navigate("/roles")}
              >
                Cancel
              </Button>
            </div>
          </Card>
        </form>
      )}
    </div>
  );
};

export default RolePermissionMatrix;
